import { omit } from 'lodash';
import { all, call, put, takeEvery } from 'redux-saga/effects';

import { REDUX_STORE } from '@funnels/constants';
import {
  BrokerInterviewsGuarantorApiResponse,
  BrokerInterviewsGuarantorApiResponseList,
  BrokerInterviewsGuarantorApiResponseSingleEntity,
  BrokerInterviewsGuarantorWithId,
  DeleteBrokerInterviewsGuarantorAction,
  FetchAllBrokerInterviewsGuarantorAction,
  FetchBrokerInterviewsGuarantorAction,
  OmitFields,
  UpsertBrokerInterviewsGuarantorAction,
} from '@funnels/types';
import { datadogUtil } from '@funnels/utils';

import { waitForAuth } from '../_common';
import {
  deleteBrokerInterviewsGuarantorFail,
  deleteBrokerInterviewsGuarantorSuccess,
  fetchAllBrokerInterviewsGuarantorFail,
  fetchAllBrokerInterviewsGuarantorSuccess,
  fetchBrokerInterviewsGuarantorFail,
  fetchBrokerInterviewsGuarantorSuccess,
  upsertBrokerInterviewsGuarantorFail,
  upsertBrokerInterviewsGuarantorSuccess,
} from './actions';
import BrokerInterviewsGuarantorAPI from './api';

const omitFields: Array<keyof OmitFields> = [
  'meta',
  'updatedAt',
  'createdAt',
  'isDeleted',
  'isDeidentified',
  'applicationId',
];

export function* deleteBrokerInterviewsGuarantorWorker(action: DeleteBrokerInterviewsGuarantorAction) {
  try {
    yield waitForAuth();
    const { applicationId, applicantId } = action;

    yield call(BrokerInterviewsGuarantorAPI.del, applicationId, applicantId);

    yield put(deleteBrokerInterviewsGuarantorSuccess(applicantId));
  } catch (e: any) {
    datadogUtil.addError(e);

    const { applicantId } = action;
    yield put(deleteBrokerInterviewsGuarantorFail(applicantId, e.response?.data || e.message));
  }
}

export function* fetchBrokerInterviewsGuarantorWorker(action: FetchBrokerInterviewsGuarantorAction): any {
  try {
    yield waitForAuth();

    const { applicationId, applicantId } = action;
    const { data }: BrokerInterviewsGuarantorApiResponse = yield call(
      BrokerInterviewsGuarantorAPI.fetchByApplicant,
      applicationId,
      applicantId
    );
    const brokerInterviewsGuarantor: BrokerInterviewsGuarantorWithId = omit(data, omitFields);

    yield put(fetchBrokerInterviewsGuarantorSuccess(brokerInterviewsGuarantor));
  } catch (e: any) {
    datadogUtil.addError(e);

    const { applicantId } = action;
    yield put(fetchBrokerInterviewsGuarantorFail(applicantId, e.response?.data || e.message));
  }
}

export function* fetchAllBrokerInterviewsGuarantorWorker(action: FetchAllBrokerInterviewsGuarantorAction): any {
  try {
    yield waitForAuth();
    const { applicationId } = action;

    const { data }: BrokerInterviewsGuarantorApiResponseList = yield call(
      BrokerInterviewsGuarantorAPI.fetchAll,
      applicationId
    );

    yield put(fetchAllBrokerInterviewsGuarantorSuccess(data.map((entry) => omit(entry, omitFields))));
  } catch (e: any) {
    datadogUtil.addError(e);

    const { applicationId } = action;
    yield put(fetchAllBrokerInterviewsGuarantorFail(applicationId, e.response?.data || e.message));
  }
}

export function* upsertBrokerInterviewsGuarantorWorker(action: UpsertBrokerInterviewsGuarantorAction) {
  try {
    yield waitForAuth();
    const { applicationId, brokerInterviewsGuarantorList } = action;

    const upsertRequestList = brokerInterviewsGuarantorList.map(({ applicantId, ...interviewQuestions }) =>
      call(BrokerInterviewsGuarantorAPI.upsert, applicationId, applicantId, interviewQuestions)
    );

    const response: Array<{ data: BrokerInterviewsGuarantorApiResponseSingleEntity }> = yield all(upsertRequestList);

    yield put(upsertBrokerInterviewsGuarantorSuccess(response.map(({ data }) => omit(data, omitFields))));
  } catch (e: any) {
    datadogUtil.addError(e);

    const { applicationId } = action;
    yield put(upsertBrokerInterviewsGuarantorFail(applicationId, e.response?.data || e.message));
  }
}

export function* brokerInterviewsGuarantorWatcher() {
  yield takeEvery(
    REDUX_STORE.BROKER_INTERVIEWS_GUARANTOR.ACTION_TYPES.DELETE_BROKER_INTERVIEWS_GUARANTOR,
    deleteBrokerInterviewsGuarantorWorker
  );
  yield takeEvery(
    REDUX_STORE.BROKER_INTERVIEWS_GUARANTOR.ACTION_TYPES.FETCH_BROKER_INTERVIEWS_GUARANTOR,
    fetchBrokerInterviewsGuarantorWorker
  );
  yield takeEvery(
    REDUX_STORE.BROKER_INTERVIEWS_GUARANTOR.ACTION_TYPES.FETCH_ALL_BROKER_INTERVIEWS_GUARANTOR,
    fetchAllBrokerInterviewsGuarantorWorker
  );
  yield takeEvery(
    REDUX_STORE.BROKER_INTERVIEWS_GUARANTOR.ACTION_TYPES.UPSERT_BROKER_INTERVIEWS_GUARANTOR,
    upsertBrokerInterviewsGuarantorWorker
  );
}

export default brokerInterviewsGuarantorWatcher;
